<template>
  <q-layout view="hHr LpR fFr">
    <!-- ===== NAVIGATION ================================================== -->

    <!-- Header -->
    <q-header v-if="user" reveal>
      <j-header
        @toggle-menu="drawerOpen = !drawerOpen"
        @request-logout="requestLogout"
      />
    </q-header>

    <!-- Side drawer -->
    <q-drawer
      v-model="drawerOpen"
      overlay
      side="right"
    >
      <j-drawer
        @toggle-menu="drawerOpen = !drawerOpen"
        @request-logout="requestLogout"
      />
    </q-drawer>

    <!-- ===== DEVELOPMENT RIBBON ========================================== -->

    <div v-if="user && client.environment === 'development'" class="ribbon">
      <div>Development</div>
    </div>

    <!-- ===== CONTENT ===================================================== -->

    <q-page-container>
      <!-- If we have a "user" display the router view... -->
      <router-view v-if="user" />
      <!-- ...otherwise show our login page -->
      <j-login v-else />
    </q-page-container>
  </q-layout>
</template>

<script setup lang="ts">
import { useTranslator } from '@juit/vue-i18n'
import { useQuasar } from 'quasar'
import { onMounted, onUnmounted, shallowRef } from 'vue'
import { useRouter } from 'vue-router'

import { client, user } from '@/init/client'
import { env } from '@/init/env'
import { log } from '@/init/sentry'
import JDrawer from '@/layout/drawer.vue'
import JHeader from '@/layout/header.vue'
import JLogin from '@/login.vue'


const { t } = useTranslator()
const { notify, dialog } = useQuasar()
const router = useRouter()

async function checkReload(): Promise<void> {
  try {
    const response = await fetch('/build.json')
    const data = await response.json()
    log('Reload check', data)
    if ((data?.timestamp) && (data.timestamp !== env.VITE_BUILD_TIMESTAMP)) {
      return void notify({
        message: t({ en: 'New version available', de: 'Neue Version verfügbar' }),
        caption: t({ en: 'Please refresh the page', de: 'Bitte aktualisieren Sie die Seite' }),
        icon: 'sym_r_cycle',
        color: 'primary',
        group: false,
        timeout: 0,
        actions: [ {
          label: t({ en: 'Refresh', de: 'Aktualisieren' }),
          handler: () => window.location.reload(),
          color: 'white',
        } ],
      })
    }
  } catch (error) {
    log('Failed to check for new version', error)
  }

  // Check again in 60 seconds
  setTimeout(checkReload, 60000)
}

onMounted(() => {
  log('App mounted')

  // Start live reload
  checkReload()
})

onUnmounted(() => {
  log('App unmounted')
})

/** Display the "logout" confirmation and (if confirmed) logout the user */
function requestLogout(): void {
  dialog({
    title: t({ en: 'Logout', de: 'Logout' }),
    message: t({
      en: 'Are you sure you want to logout?',
      de: 'Möchten Sie sich wirklich ausloggen?',
    }),
    cancel: true,
    persistent: true,
  }).onOk(()=>{
    client.oauth.logout()
    router.replace({ name: 'Home' })
  })
}

/** Whether the drawer is open or not */
const drawerOpen = shallowRef(false)
</script>

<style scoped lang="css">
.ribbon {
  pointer-events: none;
  position: fixed;
  z-index: 5000;

  height: 20px;
  top: 25px;
  width: 300px;
  left: -115px;
  transform: rotate(-45deg);

  color: white;
  background-color: var(--q-primary);
  opacity: 0.75;
  text-align: center;
  text-transform: uppercase;
  font-weight: 600;

  div {
    font-size: 10px;
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translateX(-50%) translateY(-50%);
  }
}
</style>
